/**
 * キーワードに空白文字（半角スペース、全角スペース）が含まれているかチェックする
 * @param keyword チェック対象のキーワード
 * @returns 空白文字が含まれている場合はtrue、それ以外はfalse
 */
export function containsWhitespace(keyword: string): boolean {
  // 半角スペースまたは全角スペースが含まれているかチェック
  return /[\s\u3000]/.test(keyword)
}

/**
 * キーワードに不可視文字（ゼロ幅文字など）が含まれているかチェックする
 * @param keyword チェック対象のキーワード
 * @returns 不可視文字が含まれている場合はtrue、それ以外はfalse
 */
export function containsInvisibleChars(keyword: string): boolean {
  return (
    keyword
      .replace(/\u200B/g, '') // ゼロ幅スペース
      .replace(/\u200C/g, '') // ゼロ幅非接合子
      .replace(/\u200D/g, '') // ゼロ幅接合子
      .replace(/\uFEFF/g, '') // BOM
      .trim() !== keyword
  )
}

/**
 * キーワードに禁止された特殊記号が含まれているかチェックする
 * @param keyword チェック対象のキーワード
 * @returns 禁止記号が含まれている場合はtrue、それ以外はfalse
 */
export function containsInvalidSymbols(keyword: string): boolean {
  // 禁止記号のパターン: !, ?, ., ,, @, $, %, &, *, +, =, -, (), [], {}, |, \, /, :, ;, ", ', <, >
  const invalidSymbolsPattern = /[!?.,@$%&*+=\-()[\]{}|\\/:;"'<>]/
  return invalidSymbolsPattern.test(keyword)
}

/**
 * キーワードに絵文字が含まれているかチェックする
 * @param keyword チェック対象のキーワード
 * @returns 絵文字が含まれている場合はtrue、それ以外はfalse
 */
export function containsEmoji(keyword: string): boolean {
  // Unicode絵文字の範囲をカバーする正規表現
  const emojiPattern =
    /[\u{1F000}-\u{1F9FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F300}-\u{1F5FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}]/u
  return emojiPattern.test(keyword)
}

/**
 * キーワードが数字のみで構成されているかチェックする
 * @param keyword チェック対象のキーワード
 * @returns 数字のみの場合はtrue、それ以外はfalse
 */
export function isNumbersOnly(keyword: string): boolean {
  return keyword.length > 0 && /^\d+$/.test(keyword)
}

/**
 * キーワードがアンダースコアのみで構成されているかチェックする
 * @param keyword チェック対象のキーワード
 * @returns アンダースコアのみの場合はtrue、それ以外はfalse
 */
export function isUnderscoreOnly(keyword: string): boolean {
  return keyword.length > 0 && /^_+$/.test(keyword)
}

/**
 * キーワードがアンダースコアで開始または終了するかチェックする
 * @param keyword チェック対象のキーワード
 * @returns アンダースコアで開始または終了する場合はtrue、それ以外はfalse
 */
export function startsOrEndsWithUnderscore(keyword: string): boolean {
  return keyword.length > 0 && /^_|_$/.test(keyword)
}

/**
 * キーワードに複数のハッシュタグ記号が含まれているかチェックする
 * @param keyword チェック対象のキーワード
 * @returns 複数のハッシュタグ記号が含まれている場合はtrue、それ以外はfalse
 */
export function hasMultipleHashtags(keyword: string): boolean {
  // 連続するハッシュタグ（##）をチェック
  return /##|＃＃/.test(keyword)
}

/**
 * キーワードが有効かどうか総合的にチェックする
 * @param keyword チェック対象のキーワード
 * @param skipWhitespace 空白文字チェックをスキップするかどうか
 * @returns 有効な場合はtrue、無効な場合はfalse
 */
export function isValidKeyword(keyword: string, skipWhitespace = false): boolean {
  return !getKeywordInvalidReason(keyword, 'strict', !skipWhitespace)
}

/**
 * キーワードバリデーションの結果インターフェース
 */
interface KeywordValidationResult {
  isValid: boolean
  errorMessage?: string
}

type KeywordValidationMode = 'strict' | 'whitespace_and_length_only'
type KeywordInvalidReason = 'whitespace' | 'invalid'

/**
 * キーワードバリデーションのオプション
 */
interface KeywordValidationOptions {
  /** 最大文字数（デフォルト: 120） */
  maxLength?: number
  /** 空白文字チェックを行うかどうか（デフォルト: true） */
  checkWhitespace?: boolean
  /** バリデーションモード（デフォルト: strict） */
  mode?: KeywordValidationMode
  /** エラーメッセージのカスタマイズ */
  messages?: {
    maxLengthExceeded?: string
    containsWhitespace?: string
    containsInvalidChars?: string
  }
}

/**
 * キーワードの不正理由を取得する
 * @param keyword チェック対象のキーワード
 * @param mode バリデーションモード
 * @param checkWhitespace 空白文字チェックを行うかどうか
 * @returns 不正理由（不正がない場合はundefined）
 */
function getKeywordInvalidReason(
  keyword: string,
  mode: KeywordValidationMode,
  checkWhitespace: boolean
): KeywordInvalidReason | undefined {
  if (keyword.length === 0) return undefined

  if (checkWhitespace && containsWhitespace(keyword)) return 'whitespace'
  if (containsInvisibleChars(keyword)) return 'invalid'

  if (mode === 'whitespace_and_length_only') {
    return undefined
  }

  if (containsInvalidSymbols(keyword)) return 'invalid'
  if (containsEmoji(keyword)) return 'invalid'
  if (isNumbersOnly(keyword)) return 'invalid'
  if (isUnderscoreOnly(keyword)) return 'invalid'
  if (startsOrEndsWithUnderscore(keyword)) return 'invalid'
  if (hasMultipleHashtags(keyword)) return 'invalid'

  return undefined
}

/**
 * キーワードの配列をバリデーションする
 * @param keywords チェック対象のキーワード配列
 * @param origin 元のキーワード配列（削除操作の判定用）
 * @param options バリデーションオプション
 * @returns バリデーション結果
 */
export function validateKeywords(
  keywords: string[],
  origin: string[],
  options: KeywordValidationOptions = {}
): KeywordValidationResult {
  const maxLength = options.maxLength || 120
  const checkWhitespace = options.checkWhitespace !== false
  const mode = options.mode || 'strict'
  const messages = options.messages || {}

  const defaultMessages = {
    maxLengthExceeded: messages.maxLengthExceeded || `応募キーワードの合計を${maxLength}文字以下にしてください。`,
    containsWhitespace: messages.containsWhitespace || 'キーワードに利用できない文字列が含まれています。',
    containsInvalidChars: messages.containsInvalidChars || 'キーワードに利用できない文字列が含まれています。'
  }

  // 削除操作の場合は、残りのチェックをスキップ
  if (origin.length > keywords.length) {
    return { isValid: true }
  }

  // 重複チェック（最後に追加されたキーワードが既に存在する場合）
  if (keywords.length > 0 && origin.includes(keywords[keywords.length - 1])) {
    return { isValid: false }
  }

  // 各キーワードの有効性チェック
  for (const keyword of keywords) {
    // 空白のみ入力がtrim後に空文字になったケースを弾く
    if (keyword.length === 0) {
      return {
        isValid: false,
        errorMessage: defaultMessages.containsWhitespace
      }
    }

    const invalidReason = getKeywordInvalidReason(keyword, mode, checkWhitespace)
    if (invalidReason) {
      return {
        isValid: false,
        errorMessage:
          invalidReason === 'whitespace' ? defaultMessages.containsWhitespace : defaultMessages.containsInvalidChars
      }
    }
  }

  // 合計文字数チェック
  const totalLength = keywords.join('').length
  if (totalLength > maxLength) {
    return {
      isValid: false,
      errorMessage: defaultMessages.maxLengthExceeded
    }
  }

  return { isValid: true }
}

/**
 * 単一のキーワードをバリデーションする
 * @param keyword チェック対象のキーワード
 * @param options バリデーションオプション
 * @returns バリデーション結果
 */
export function validateSingleKeyword(
  keyword: string,
  options: KeywordValidationOptions = {}
): KeywordValidationResult {
  const maxLength = options.maxLength || 120
  const checkWhitespace = options.checkWhitespace !== false
  const mode = options.mode || 'strict'
  const messages = options.messages || {}

  const defaultMessages = {
    maxLengthExceeded: messages.maxLengthExceeded || `キーワードは${maxLength}文字以下にしてください。`,
    containsWhitespace: messages.containsWhitespace || 'キーワードに利用できない文字列が含まれています。',
    containsInvalidChars: messages.containsInvalidChars || 'キーワードに利用できない文字列が含まれています。'
  }

  // 文字数チェック
  if (keyword.length > maxLength) {
    return {
      isValid: false,
      errorMessage: defaultMessages.maxLengthExceeded
    }
  }

  // キーワードの有効性チェック
  const invalidReason = getKeywordInvalidReason(keyword, mode, checkWhitespace)
  if (invalidReason) {
    return {
      isValid: false,
      errorMessage:
        invalidReason === 'whitespace' ? defaultMessages.containsWhitespace : defaultMessages.containsInvalidChars
    }
  }

  return { isValid: true }
}
